import React from 'react';
import type { match } from '@shared/core';
import { Redirect, Route, Switch } from '@shared/core';
import { isValidShopPageMatch, useCardsRouterContext } from '@apps/card/Card.routes';
import { DesignsGallery } from './DesignsGallery';
import { ExistingDraftsWarningProvider } from '../ExistingDraftsWarningProvider';
import { RestrictedInternationalShippingWarningProvider } from '../RestrictedInternationalShippingWarningProvider';
import { ProductDetailsPage } from '../ProductDetailsPage/ProductDetailsPage';
import type { CustomizeNowClickOverrideHandler } from './DesignsGallery.types';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { CategoryNavSwitch } from '../CategoryNav';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { CardDiscountProvider } from './useCardDiscountContext';
import { SearchProvider } from './components/Search/SearchContext';
import { WebsiteThemeIdProvider } from './WebsiteThemeIdContext';

interface ShopProps {
  eventId?: string;
  onCustomizeClickCardOverride?: CustomizeNowClickOverrideHandler;
  match: match;
}

export const ShopRouter = (props: ShopProps) => {
  const { eventId, onCustomizeClickCardOverride, match } = props;
  const { routes, getShopPath } = useCardsRouterContext();
  const { currentCategory } = useCardCategoryContext();

  if (!isValidShopPageMatch(match.params)) {
    // TODO: Discuss with team about showing a 404 page or redirecting to a known shop page
    return <Redirect to={getShopPath('wedding', StationeryTemplateCategoryEnum.saveTheDate)} />;
  }

  return (
    <CardDiscountProvider>
      <RestrictedInternationalShippingWarningProvider eventId={eventId}>
        <ExistingDraftsWarningProvider eventId={eventId}>
          <Switch>
            <Route
              path={routes.productDetailsPageV2.paths}
              render={({ match }) => (
                <ProductDetailsPage
                  eventId={props.eventId}
                  themeId={match.params.themeId}
                  templateId={match.params.templateId}
                  onCustomizeCardClickOverride={onCustomizeClickCardOverride}
                />
              )}
            />
            {/* Legacy routing for pdp prior to including templateId in the URL  */}
            <Route
              path={routes.productDetailsPage.paths}
              render={({ match }) => (
                <ProductDetailsPage
                  eventId={props.eventId}
                  themeId={match.params.themeId}
                  templateId={null}
                  onCustomizeCardClickOverride={onCustomizeClickCardOverride}
                />
              )}
            />
            <Route
              exact={true}
              path={routes.designsGallery.paths}
              render={({ match }) => (
                <>
                  <CategoryNavSwitch />
                  <SearchProvider>
                    <WebsiteThemeIdProvider eventHandle={match.params.eventHandle}>
                      <DesignsGallery
                        onCustomizeCardClickOverride={onCustomizeClickCardOverride}
                        eventId={eventId}
                        eventHandle={match.params.eventHandle}
                      />
                    </WebsiteThemeIdProvider>
                  </SearchProvider>
                </>
              )}
            />
            <Redirect to={getShopPath('wedding', currentCategory)} />
          </Switch>
        </ExistingDraftsWarningProvider>
      </RestrictedInternationalShippingWarningProvider>
    </CardDiscountProvider>
  );
};
